import * as React from 'react';
import { Component } from 'react';
import { History } from 'history';
import { getFromHistory } from '../../helpers/history/history';
import {
  resetPassword,
  resetRequestPassword,
  validateResetPasswordToken
} from '../../helpers/service/public/password';
import './ResetPassword.scss';

interface Props {
  history: History;
}

type ResetStep = 'request' | 'sent' | 'set' | 'done';
type ResetError = 'email' | 'expired' | 'mismatch' | 'weak' | 'request' | '';
type PasswordFlow = 'reset' | 'setup';

interface State {
  secretKey: string;
  step: ResetStep;
  email: string;
  password: string;
  confirmPassword: string;
  isPasswordVisible: boolean;
  error: ResetError;
  errorMessage: string;
  isLoading: boolean;
  isValidatingToken: boolean;
  cooldown: number;
  wasResent: boolean;
  flow: PasswordFlow;
  source: string;
}

const Icon: React.FC<{ name: string; size?: number }> = ({ name, size = 18 }) => {
  const paths = {
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m4 7 8 6 8-6" />
      </>
    ),
    lock: (
      <>
        <rect x="5" y="10" width="14" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
    eye: (
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    eyeOff: (
      <>
        <path d="m3 3 18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.4 5.3A9.4 9.4 0 0 1 12 5c6.5 0 10 7 10 7a16.4 16.4 0 0 1-3 3.8M6.2 6.2A16.7 16.7 0 0 0 2 12s3.5 7 10 7a9.5 9.5 0 0 0 3.3-.6" />
      </>
    ),
    alert: (
      <>
        <path d="m12 3 10 18H2L12 3Z" />
        <path d="M12 9v5M12 18h.01" />
      </>
    ),
    shield: <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />,
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    send: <path d="m3 11 18-8-8 18-2-8-8-2Zm8 2 5-5" />,
    arrow: (
      <>
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
      </>
    ),
    back: (
      <>
        <path d="M19 12H5" />
        <path d="m11 18-6-6 6-6" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />
  };

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
};

export class ResetPassword extends Component<Props, State> {
  headingRef = React.createRef<HTMLHeadingElement>();
  cooldownTimer: number;

  constructor(props) {
    super(props);
    const secretKey = getFromHistory(props.history, 'secretKey');
    const flow = getFromHistory(props.history, 'mode') === 'setup' ? 'setup' : 'reset';
    const source = getFromHistory(props.history, 'source') || '';
    this.state = {
      secretKey,
      step: secretKey ? 'set' : 'request',
      email: '',
      password: '',
      confirmPassword: '',
      isPasswordVisible: false,
      error: '',
      errorMessage: '',
      isLoading: false,
      isValidatingToken: Boolean(secretKey),
      cooldown: 0,
      wasResent: false,
      flow,
      source
    };
  }

  componentDidMount(): void {
    this.focusHeading();
    if (this.state.secretKey) this.validateToken();
  }

  validateToken = (): void => {
    validateResetPasswordToken(this.state.secretKey)
      .then(() => this.setState({ isValidatingToken: false }))
      .catch(error => {
        const message = this.getErrorMessage(error);
        const expired = /expired|already been used/i.test(message);
        this.setState({
          isValidatingToken: false,
          error: expired ? 'expired' : 'request',
          errorMessage: expired ? '' : message
        });
      });
  };

  componentDidUpdate(_prevProps: Props, prevState: State): void {
    if (prevState.step !== this.state.step) this.focusHeading();
  }

  componentWillUnmount(): void {
    if (this.cooldownTimer) window.clearInterval(this.cooldownTimer);
  }

  focusHeading = (): void => {
    window.setTimeout(() => this.headingRef.current && this.headingRef.current.focus(), 0);
  };

  redirectToLoginPage = (): void => {
    this.props.history.push('/login');
  };

  isSetupFlow = (): boolean => this.state.flow === 'setup';

  isStudentSetupFlow = (): boolean => {
    return this.isSetupFlow() && ['activation', 'student', 'under13'].indexOf(this.state.source) !== -1;
  };

  getErrorMessage = (error): string => {
    return (
      error?.response?.data?.details?.text ||
      error?.response?.data?.error?.details?.text ||
      error?.response?.data?.error?.message ||
      error?.response?.data?.message ||
      error?.message ||
      'Something went wrong. Please try again.'
    );
  };

  startCooldown = (seconds: number): void => {
    if (this.cooldownTimer) window.clearInterval(this.cooldownTimer);
    this.setState({ cooldown: Math.max(1, Number(seconds) || 60) });
    this.cooldownTimer = window.setInterval(() => {
      this.setState(current => {
        if (current.cooldown <= 1) {
          window.clearInterval(this.cooldownTimer);
          return { cooldown: 0 } as Pick<State, 'cooldown'>;
        }
        return { cooldown: current.cooldown - 1 } as Pick<State, 'cooldown'>;
      });
    }, 1000);
  };

  requestReset = (event?: React.SyntheticEvent): void => {
    if (event) event.preventDefault();
    const email = this.state.email.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      this.setState({ error: 'email', errorMessage: '' });
      return;
    }

    this.setState({ isLoading: true, error: '', errorMessage: '' });
    resetRequestPassword(email, this.isSetupFlow() ? 'PASSWORD_SETUP' : 'PASSWORD_RESET')
      .then(response => {
        this.setState({ step: 'sent', email, isLoading: false, wasResent: false });
        this.startCooldown(response?.retryAfterSeconds || 60);
      })
      .catch(() => {
        this.setState({
          error: 'request',
          errorMessage: 'We could not request a reset link just now. Please try again.',
          isLoading: false
        });
      });
  };

  resend = (): void => {
    const { email, cooldown, isLoading } = this.state;
    if (cooldown > 0 || isLoading) return;
    this.setState({ isLoading: true, error: '', errorMessage: '' });
    resetRequestPassword(email, this.isSetupFlow() ? 'PASSWORD_SETUP' : 'PASSWORD_RESET')
      .then(response => {
        this.setState({ isLoading: false, wasResent: true });
        this.startCooldown(response?.retryAfterSeconds || 60);
      })
      .catch(() => {
        this.setState({
          error: 'request',
          errorMessage: 'We could not resend the link just now. Please try again.',
          isLoading: false
        });
      });
  };

  useDifferentAddress = (): void => {
    if (this.cooldownTimer) window.clearInterval(this.cooldownTimer);
    this.setState({ step: 'request', cooldown: 0, wasResent: false, error: '', errorMessage: '' });
  };

  getPasswordStrength = (): number => {
    const password = this.state.password;
    if (!password) return 0;
    let score = password.length >= 8 ? 1 : 0;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
    if (/\d/.test(password) && /[^A-Za-z0-9]/.test(password)) score += 1;
    return Math.max(1, Math.min(4, score));
  };

  isPasswordAllowed = (): boolean => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/.test(this.state.password);
  };

  savePassword = (event?: React.SyntheticEvent): void => {
    if (event) event.preventDefault();
    const { password, confirmPassword, secretKey } = this.state;
    if (!this.isPasswordAllowed()) {
      this.setState({ error: 'weak', errorMessage: '' });
      return;
    }
    if (password !== confirmPassword) {
      this.setState({ error: 'mismatch', errorMessage: '' });
      return;
    }

    this.setState({ isLoading: true, error: '', errorMessage: '' });
    resetPassword(password, secretKey)
      .then(() => {
        this.setState({ step: 'done', isLoading: false, password: '', confirmPassword: '' });
      })
      .catch(error => {
        const message = this.getErrorMessage(error);
        const expired = /expired|already been used/i.test(message);
        this.setState({
          error: expired ? 'expired' : 'request',
          errorMessage: expired ? '' : message,
          isLoading: false
        });
      });
  };

  requestNewLink = (): void => {
    const { flow, source } = this.state;
    const query = flow === 'setup' ? `?mode=setup${source ? `&source=${encodeURIComponent(source)}` : ''}` : '';
    this.props.history.push(`/resetPassword${query}`);
    this.setState({
      secretKey: undefined,
      step: 'request',
      password: '',
      confirmPassword: '',
      error: '',
      errorMessage: ''
    });
  };

  renderSteps(): React.ReactNode {
    const { step } = this.state;
    const order: ResetStep[] = ['request', 'sent', 'set'];
    const currentIndex = step === 'done' ? 3 : order.indexOf(step);
    const steps = this.isSetupFlow()
      ? [
          ['Enter your email', 'Use the email linked to your account'],
          ['Open the email', 'Follow the secure, single-use link'],
          ['Create your password', 'Then sign in as usual']
        ]
      : [
          ['Ask for a link', 'Confirm the email on your account'],
          ['Open the email', 'Valid for two hours, single use'],
          ['Set a new password', 'Then sign in as usual']
        ];

    return (
      <div className="sitResetSteps">
        {steps.map((item, index) => {
          const status = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo';
          return (
            <div className={`sitResetStep sitResetStep--${status}`} key={item[0]}>
              <span className="sitResetStep__marker">
                {status === 'done' ? <Icon name="check" size={13} /> : index + 1}
              </span>
              <span>
                <strong>{item[0]}</strong>
                <em>{item[1]}</em>
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  renderBanner(kind: 'error' | 'info', title: string, text: string): React.ReactNode {
    return (
      <div className={`sitResetBanner sitResetBanner--${kind}`}>
        <Icon name={kind === 'error' ? 'alert' : 'clock'} size={17} />
        <span>
          <strong>{title}</strong>
          <em>{text}</em>
        </span>
      </div>
    );
  }

  renderRequest(): React.ReactNode {
    const { email, error, errorMessage, isLoading } = this.state;
    const isSetup = this.isSetupFlow();
    const isStudentSetup = this.isStudentSetupFlow();
    return (
      <form className="sitResetForm" onSubmit={this.requestReset}>
        <h1 ref={this.headingRef} tabIndex={-1}>
          {isSetup ? (isStudentSetup ? 'Set up your password' : 'Set up your password') : 'Forgot your password?'}
        </h1>
        <p>
          {isSetup
            ? isStudentSetup
              ? 'Your school has already created your account. Enter the school email address linked to it and we’ll send you a secure setup link.'
              : 'Your account already exists. Enter the email address linked to it and we’ll send you a secure setup link.'
            : 'Enter the email address on your Squad In Touch account and we’ll send a link to set a new password.'}
        </p>
        {error === 'email' &&
          this.renderBanner(
            'error',
            'That doesn’t look like a valid email address.',
            isSetup
              ? 'Check for typos and use the school email address linked to your account.'
              : 'Check for typos, or use the address your school invited.'
          )}
        {error === 'request' &&
          this.renderBanner('error', isSetup ? 'We couldn’t send the setup link.' : 'We couldn’t send the reset link.', errorMessage)}
        <label className={error === 'email' ? 'sitResetField isInvalid' : 'sitResetField'}>
          <span>Email address</span>
          <div>
            <Icon name="mail" size={17} />
            <input
              type="email"
              value={email}
              placeholder="you@example.com"
              autoComplete="email"
              onChange={event => this.setState({ email: event.target.value, error: '', errorMessage: '' })}
              autoFocus
            />
          </div>
        </label>
        <button className="sitResetPrimary" type="submit" disabled={isLoading}>
          {isLoading ? 'Sending…' : isSetup ? 'Send setup link' : 'Send reset link'}
          {!isLoading && <Icon name="arrow" size={17} />}
        </button>
        <button className="sitResetBack" type="button" onClick={this.redirectToLoginPage}>
          <Icon name="back" size={15} />
          Back to sign in
        </button>
      </form>
    );
  }

  renderSent(): React.ReactNode {
    const { email, cooldown, wasResent, isLoading, error, errorMessage } = this.state;
    const isSetup = this.isSetupFlow();
    const isStudentSetup = this.isStudentSetupFlow();
    return (
      <div className="sitResetForm">
        <span className="sitResetStateIcon sitResetStateIcon--info">
          <Icon name="send" size={23} />
        </span>
        <h1 ref={this.headingRef} tabIndex={-1}>
          {isStudentSetup ? 'Check your school inbox' : 'Check your inbox'}
        </h1>
        <p>
          If <strong>{email}</strong> {isStudentSetup ? 'matches a student account' : 'has an account'}, a{' '}
          {isSetup ? 'password setup' : 'reset'} link is on its way. Open it to{' '}
          {isSetup ? 'create your password' : 'choose a new password'}.
        </p>
        {this.renderBanner(
          'info',
          'The link works once and expires in two hours.',
          isSetup ? 'Your account stays protected until you create your password.' : 'Nothing changes until you set the new password.'
        )}
        {error === 'request' &&
          this.renderBanner('error', isSetup ? 'We couldn’t resend the setup link.' : 'We couldn’t resend the link.', errorMessage)}
        <p className="sitResetSupport">
          Not there in a few minutes? Check spam, or{' '}
          <button type="button" onClick={this.useDifferentAddress}>
            {isStudentSetup ? 'try a different school email' : 'try a different address'}
          </button>
          .
        </p>
        <button className="sitResetSecondary" type="button" onClick={this.resend} disabled={cooldown > 0 || isLoading}>
          {isLoading
            ? 'Sending…'
            : cooldown > 0
            ? `${wasResent ? 'Link sent again — r' : 'R'}esend in ${cooldown}s`
            : 'Resend link'}
        </button>
        <button className="sitResetBack" type="button" onClick={this.redirectToLoginPage}>
          <Icon name="back" size={15} />
          Back to sign in
        </button>
      </div>
    );
  }

  renderSet(): React.ReactNode {
    const { password, confirmPassword, isPasswordVisible, error, errorMessage, isLoading } = this.state;
    const isSetup = this.isSetupFlow();
    const isStudentSetup = this.isStudentSetupFlow();
    const strength = this.getPasswordStrength();
    const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
    const matches = Boolean(confirmPassword) && password === confirmPassword;
    return (
      <form className="sitResetForm" onSubmit={this.savePassword}>
        <h1 ref={this.headingRef} tabIndex={-1}>
          {isSetup ? 'Create your password' : 'Set a new password'}
        </h1>
        <p>
          {isSetup
            ? isStudentSetup
              ? 'Your student account is ready. Create a strong password to finish setting it up.'
              : 'Your account is ready. Create a strong password to finish setting it up.'
            : 'Choose a strong password you haven’t used for your Squad In Touch account before.'}
        </p>
        {error === 'expired' &&
          this.renderBanner(
            'error',
            isSetup ? 'This password setup link has expired.' : 'This reset link has expired.',
            isSetup ? 'Request a fresh setup link to continue.' : 'Links last two hours. Request a fresh one to continue.'
          )}
        {error === 'mismatch' &&
          this.renderBanner(
            'error',
            'The two passwords don’t match.',
            'Retype the confirmation, or reveal both to compare.'
          )}
        {error === 'weak' &&
          this.renderBanner(
            'error',
            'Too easy to guess.',
            'Use at least 8 characters with uppercase and lowercase letters and a number.'
          )}
        {error === 'request' &&
          this.renderBanner('error', isSetup ? 'We couldn’t create your password.' : 'We couldn’t change your password.', errorMessage)}
        {error !== 'expired' && (
          <>
            <label className={error === 'weak' ? 'sitResetField isInvalid' : 'sitResetField'}>
              <span>{isSetup ? 'Password' : 'New password'}</span>
              <div>
                <Icon name="lock" size={17} />
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  value={password}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  onChange={event => this.setState({ password: event.target.value, error: '', errorMessage: '' })}
                  autoFocus
                />
                <button
                  type="button"
                  aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                  onClick={() => this.setState({ isPasswordVisible: !isPasswordVisible })}
                >
                  <Icon name={isPasswordVisible ? 'eyeOff' : 'eye'} size={17} />
                </button>
              </div>
            </label>
            <div className={`sitResetStrength sitResetStrength--${strength}`}>
              <span>
                {[1, 2, 3, 4].map(segment => (
                  <i className={segment <= strength ? 'isFilled' : ''} key={segment} />
                ))}
              </span>
              <em>{strengthLabels[strength]}</em>
            </div>
            <label className={error === 'mismatch' ? 'sitResetField isInvalid' : 'sitResetField'}>
              <span>{isSetup ? 'Confirm password' : 'Confirm new password'}</span>
              <div>
                <Icon name="lock" size={17} />
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  value={confirmPassword}
                  placeholder="Type it again"
                  autoComplete="new-password"
                  onChange={event =>
                    this.setState({ confirmPassword: event.target.value, error: '', errorMessage: '' })
                  }
                />
              </div>
            </label>
            <div className={matches ? 'sitResetMatch isMatching' : 'sitResetMatch'}>
              {matches && <Icon name="check" size={13} />}
              {confirmPassword ? (matches ? 'Passwords match' : 'Doesn’t match yet') : ''}
            </div>
          </>
        )}
        {error === 'expired' ? (
          <button className="sitResetPrimary" type="button" onClick={this.requestNewLink}>
            {isSetup ? 'Request a new setup link' : 'Request a new link'}
            <Icon name="arrow" size={17} />
          </button>
        ) : (
          <button className="sitResetPrimary" type="submit" disabled={isLoading}>
            {isLoading ? 'Saving…' : isSetup ? 'Create password' : 'Save new password'}
            {!isLoading && <Icon name="arrow" size={17} />}
          </button>
        )}
      </form>
    );
  }

  renderTokenCheck(): React.ReactNode {
    return (
      <div className="sitResetForm" aria-live="polite">
        <span className="sitResetStateIcon sitResetStateIcon--info">
          <Icon name="shield" size={23} />
        </span>
        <h1 ref={this.headingRef} tabIndex={-1}>Checking your secure link</h1>
        <p>Please wait while we confirm that this link is still valid.</p>
      </div>
    );
  }

  renderDone(): React.ReactNode {
    const isSetup = this.isSetupFlow();
    const isStudentSetup = this.isStudentSetupFlow();
    return (
      <div className="sitResetForm">
        <span className="sitResetStateIcon sitResetStateIcon--success">
          <Icon name="check" size={26} />
        </span>
        <h1 ref={this.headingRef} tabIndex={-1}>
          {isSetup ? 'Password created' : 'Password changed'}
        </h1>
        <p>
          {isSetup
            ? `${isStudentSetup ? 'Your student account' : 'Your account'} is ready. You can sign in with your new password now.`
            : 'You can sign in with your new password now. Other devices where you were signed in have been signed out.'}
        </p>
        <button className="sitResetPrimary" type="button" onClick={this.redirectToLoginPage}>
          Go to sign in
          <Icon name="arrow" size={17} />
        </button>
        {!isSetup && (
          <div className="sitResetConcern">Didn’t make this change? Contact your school office straight away.</div>
        )}
      </div>
    );
  }

  render() {
    const { step, isValidatingToken } = this.state;
    const isSetup = this.isSetupFlow();
    const isStudentSetup = this.isStudentSetupFlow();
    return (
      <div className="sitResetPage">
        <div className="sitResetCard">
          <aside className="sitResetProgress">
            <img src="/dist/images/logoImage.png" alt="Squad In Touch" />
            <h2>{isSetup ? 'Set up your password' : 'Reset your password'}</h2>
            <p>
              {isSetup
                ? `${isStudentSetup ? 'Your student account already exists' : 'Your account already exists'}. Creating a password takes about a minute.`
                : 'Resetting takes about a minute. Your fixtures, squads and messages are untouched.'}
            </p>
            {this.renderSteps()}
            <div className="sitResetSecurity">
              <Icon name="shield" size={16} />
              <span>
                {isSetup
                  ? 'Password setup links are secure, single-use and time-limited. We never send passwords by email.'
                  : 'Reset links are single-use and expire after two hours. We never send passwords by email.'}
              </span>
            </div>
          </aside>
          <main className="sitResetPane">
            {isValidatingToken ? this.renderTokenCheck() : (
              <>
                {step === 'request' && this.renderRequest()}
                {step === 'sent' && this.renderSent()}
                {step === 'set' && this.renderSet()}
                {step === 'done' && this.renderDone()}
              </>
            )}
          </main>
        </div>
      </div>
    );
  }
}
